"use client"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import React from "react"
import { getNonUmbracoUrl, getUmbracoMediaUrl } from "utils"
import {
  colorPalletteIndex,
  colorPalletteIndexType,
} from "../common/bit/ButtonWithArrow"
import EnhancedTypography from "../common/bit/EnhancedTypography"
import { colors, mediaQueries, sizes } from "../theme/theme"

interface ImageCardProps {
  index: number
  data: CardProps
  desktopCardHeight?: string
}

export interface CardProps {
  title: string
  image: { url: string }
  content: string
  description: string
  category: string[]
  details?: any
  link: {
    url: string
    target: any
    name: React.JSX.Element
    text: React.JSX.Element
    href: string
  }
}

const Card = styled("a", {
  shouldForwardProp: (prop) => prop !== "activeSwiper" && prop !== "hoverColor",
})<{ activeSwiper?: boolean; hoverColor?: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: ${sizes.borderRadiusLarge};
  overflow: hidden;
  text-decoration: none;
  transition: all 0.3s ease-in-out;
  box-shadow: 9.772px -9.772px 36.646px 0px rgba(15, 36, 79, 0.08);
  &:hover {
    border: none;
    background: none;
    .bubble {
      background-color: ${(props) => props.hoverColor};
      color: ${colors.white};
    }
    img {
      transform: scale(1.02);
    }
  }
  img {
    transition: all 0.3s ease-in-out;
  }
`
const BubbleWrapper = styled(Box)`
  display: flex;
  flex-flow: row wrap;
  width: 100%;
  gap: ${sizes.baseSingle};
  z-index: 2;
  padding: ${sizes.baseSingle};
  margin: ${sizes.baseDouble};
  .bubble {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
    border-radius: 30px;
    background-color: rgba(255, 255, 255, 0.8);
    color: ${colors.black};
    padding: 2px 12px;
  }
`
const StyledWrapperImage = styled(Box)`
  position: relative;
  width: 100%;
  height: 360px;
  ${mediaQueries.mobile} {
    width: 340px;
    height: 255px;
  }
  ${mediaQueries.desktopXl} {
    height: 364px;
  }
`
const StyledImage = styled(Image)`
  object-fit: cover;
`

const StyledDetails = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${colors.white};
  width: 100%;
  height: 169px;
  padding: ${sizes.baseQuadric};
  z-index: 2;
  ${mediaQueries.mobile} {
    padding: ${sizes.baseTriple};
    height: 140px;
  }
`

const StyledDescription = styled(EnhancedTypography)`
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  text-overflow: ellipsis;
  color: ${colors.black};
  text-align: start;
  gap: ${sizes.baseTriple};
  ${mediaQueries.mobile} {
    font-size: 18px;
    gap: ${sizes.baseDouble};
  }
  ${mediaQueries.desktopM} {
    font-size: 24px;
  }
`
const StyledRedText = styled(Typography)`
  color: ${colors.red};
`

const ImageDetailsCard: React.FC<ImageCardProps> = ({ data, index }) => {
  const { title, image, category, link, details } = data
  const colorPallette = colorPalletteIndex[
    index % colorPalletteIndex.length
  ] as colorPalletteIndexType
  const t = useTranslations("SEO")
  const tra = useTranslations("ArticleBanner")
  // Format the details string according to the required pattern
  const formattedDate = details?.date?.dateTime
    ? new Date(details.date.dateTime)
        .toLocaleDateString("he-IL", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        })
        .replace(/\./g, ".")
    : ""
  const detailsString = `${formattedDate} ${details?.date?.dateTime ? "|" : ""} ${details?.author?.value || ""} ${details?.author?.value ? "|" : ""} ${details?.readingTime?.value || ""} ${tra("readingTime")}`
  return (
    <Card
      href={getNonUmbracoUrl(link?.url || link?.href || "/")}
      target={link?.target ? link?.target : "_self"}
      key={`image-card-item-${title}-${index + 1}`}
      hoverColor={colors[colorPallette]}
    >
      <StyledWrapperImage>
        {image && (
          <StyledImage
            src={getUmbracoMediaUrl(image.url)}
            alt={`${t("ArticleAlt")}${title}`}
            fill
          />
        )}
        <BubbleWrapper>
          {category &&
            category.map((item: string) => (
              <Typography
                key={`image-card-bubble-${item}-${index + 1}`}
                variant="body1"
                className="bubble"
              >
                {item}
              </Typography>
            ))}
        </BubbleWrapper>
      </StyledWrapperImage>

      <StyledDetails>
        {title && <StyledDescription variant="h3">{title}</StyledDescription>}
        {details && (
          <StyledRedText variant="body2">{detailsString}</StyledRedText>
        )}
      </StyledDetails>
    </Card>
  )
}
export default ImageDetailsCard
